import {
  cancelGenerationJob,
  createGenerationJob,
  getGenerationJobResult,
  getGenerationJobStatus,
  listActiveGenerationJobs,
  updateGenerationJobPreferences,
} from "../../api/generationJobs";
import { getStoredAccessToken } from "../../api/client";
import {
  GENERATION_JOB_POLL_INTERVAL_MS,
  isGenerationJobTerminal,
} from "./generationJobConstants";

export function isGenerationJobTrackingEnabled() {
  if (!readGenerationJobsTrackingFlag()) {
    return false;
  }
  return Boolean(getStoredAccessToken());
}

function readGenerationJobsTrackingFlag() {
  if (typeof process === "undefined" || !process.env) {
    return true;
  }
  return process.env.REACT_APP_GENERATION_JOBS_ENABLED !== "false";
}

function isDevEnvironment() {
  if (typeof process === "undefined" || !process.env) {
    return true;
  }
  return process.env.NODE_ENV !== "production";
}

async function safeCall(operation) {
  try {
    return await operation();
  } catch (error) {
    if (isDevEnvironment()) {
      console.warn("[generation-job-tracker]", error);
    }
    return null;
  }
}

export const generationJobTracker = {
  isEnabled: isGenerationJobTrackingEnabled,
  pollIntervalMs: GENERATION_JOB_POLL_INTERVAL_MS,

  async create(payload, signal) {
    if (!isGenerationJobTrackingEnabled()) {
      return null;
    }
    const response = await createGenerationJob(payload, signal);
    return response?.jobId ?? null;
  },

  async getStatus(jobId, signal) {
    try {
      return await getGenerationJobStatus(jobId, signal);
    } catch (error) {
      if (error?.status === 404) {
        return { jobId, status: "expired", notFound: true };
      }
      if (isDevEnvironment()) {
        console.warn("[generation-job-tracker]", error);
      }
      return null;
    }
  },

  async listActive(signal) {
    return safeCall(() => listActiveGenerationJobs(signal));
  },

  async getResult(jobId, signal) {
    return safeCall(() => getGenerationJobResult(jobId, signal));
  },

  async updatePreferences(jobId, saveToLibrary, signal) {
    if (!jobId) {
      return null;
    }
    return safeCall(() =>
      updateGenerationJobPreferences(jobId, { saveToLibrary }, signal),
    );
  },

  async cancel(jobId, signal) {
    if (!jobId) {
      return null;
    }
    return safeCall(() => cancelGenerationJob(jobId, signal));
  },

  isTerminalStatus(status) {
    return isGenerationJobTerminal(status);
  },
};
